import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye,
  EyeOff,
  ArrowRight,
  Loader2,
  CheckCircle2,
  Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { CloudflareTurnstile } from "@/components/auth/CloudflareTurnstile";
import { MfaSetupDialog } from "@/components/auth/MfaSetupDialog";
import {
  useAuthContext,
  SupportedAuthProvider,
} from "@/providers/AuthProvider";
import { authService } from "@/services";
import logo from "@/assets/images/logo.png";

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="currentColor"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="currentColor"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="currentColor"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

type Step = "credentials" | "new-password" | "complete";

interface FormData {
  email: string;
  tempPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface FormErrors {
  email?: string;
  tempPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
  turnstile?: string;
}

interface MfaState {
  isActive: boolean;
  session: string | null;
  secretCode: string | null;
  email: string | null;
}

export default function ActivateAccountPage() {
  const { loginWithOIDCProvider, completeEmailLogin } = useAuthContext();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("credentials");
  const [formData, setFormData] = useState<FormData>({
    email: "",
    tempPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [session, setSession] = useState<string | null>(null);
  const [mfaSetup, setMfaSetup] = useState<MfaState>({
    isActive: false,
    session: null,
    secretCode: null,
    email: null,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (errors[id as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [id]: undefined }));
    }
  };

  const validateCredentials = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.tempPassword) {
      newErrors.tempPassword = "Temporary password is required";
    }

    const isDevelopment = import.meta.env.VITE_ENVIRONMENT === "development";
    if (!turnstileToken && !isDevelopment) {
      newErrors.turnstile = "Please complete the security verification";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateNewPassword = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
    } else if (!/[A-Z]/.test(formData.newPassword)) {
      newErrors.newPassword = "Password must include an uppercase letter";
    } else if (!/[a-z]/.test(formData.newPassword)) {
      newErrors.newPassword = "Password must include a lowercase letter";
    } else if (!/[0-9]/.test(formData.newPassword)) {
      newErrors.newPassword = "Password must include a number";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateCredentials()) return;

    setIsLoading(true);

    try {
      const response = await authService.loginWithEmail({
        email: formData.email,
        password: formData.tempPassword,
        turnstileToken: turnstileToken!,
      });

      if (response.challengeName === "NEW_PASSWORD_REQUIRED") {
        setSession(response.session!);
        setStep("new-password");
      } else if (response.accessToken && response.idToken) {
        completeEmailLogin({
          accessToken: response.accessToken,
          idToken: response.idToken,
          refreshToken: response.refreshToken!,
          expiresIn: response.expiresIn!,
        });
        navigate("/");
      } else {
        setSubmitError(
          "Unexpected response. Please try again or contact support.",
        );
      }
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Failed to verify credentials",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateNewPassword()) return;

    setIsLoading(true);

    try {
      const response = await authService.respondToNewPasswordChallenge({
        session: session!,
        email: formData.email,
        newPassword: formData.newPassword,
      });

      if (response.challengeName === "MFA_SETUP") {
        const setupResponse = await authService.setupMfa({
          session: response.session!,
        });
        setMfaSetup({
          isActive: true,
          session: setupResponse.session,
          secretCode: setupResponse.secretCode,
          email: formData.email,
        });
      } else if (response.accessToken && response.idToken) {
        completeEmailLogin({
          accessToken: response.accessToken,
          idToken: response.idToken,
          refreshToken: response.refreshToken!,
          expiresIn: response.expiresIn!,
        });
        setStep("complete");
        setTimeout(() => navigate("/"), 2000);
      }
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Failed to set new password",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSetupVerify = async (
    code: string,
  ): Promise<string[] | null> => {
    if (!mfaSetup.session || !mfaSetup.email) return null;

    const response = await authService.verifyMfaSetup({
      session: mfaSetup.session,
      code,
      email: mfaSetup.email,
    });

    if (response.accessToken && response.idToken) {
      completeEmailLogin({
        accessToken: response.accessToken,
        idToken: response.idToken,
        refreshToken: response.refreshToken!,
        expiresIn: response.expiresIn!,
      });
    }

    return response.backupCodes;
  };

  const handleMfaSetupComplete = () => {
    setMfaSetup({
      isActive: false,
      session: null,
      secretCode: null,
      email: null,
    });
    setStep("complete");
    setTimeout(() => navigate("/"), 2000);
  };

  const handleTurnstileVerify = (token: string) => {
    setTurnstileToken(token);
    if (errors.turnstile) {
      setErrors((prev) => ({ ...prev, turnstile: undefined }));
    }
  };

  const handleTurnstileError = () => {
    setTurnstileToken(null);
    setErrors((prev) => ({
      ...prev,
      turnstile: "Verification failed. Please try again.",
    }));
  };

  const handleTurnstileExpire = () => {
    setTurnstileToken(null);
  };

  if (step === "complete") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <Card className="glass-card border-gradient">
            <CardContent className="pt-8 pb-8">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
              <h2 className="font-display text-2xl font-bold mb-4">
                Account Activated!
              </h2>
              <p className="text-muted-foreground mb-6">
                Your account has been successfully set up. Redirecting you to
                the portal...
              </p>
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24">
      <div className="absolute inset-0 hero-gradient" />
      <div className="absolute inset-0 grid-background opacity-40" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full relative z-10"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-8">
            <img src={logo} alt="EchoPeak Labs" className="w-16 h-16" />
          </Link>
          <h1 className="font-display text-3xl font-bold mb-2">
            Activate Your Account
          </h1>
          <p className="text-muted-foreground">
            {step === "credentials"
              ? "Enter your credentials from the welcome email"
              : "Create a secure password for your account"}
          </p>
        </div>

        <Card className="glass-card border-gradient">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              {step === "credentials"
                ? "Verify Your Identity"
                : "Set Your Password"}
            </CardTitle>
            <CardDescription>
              {step === "credentials"
                ? "Use the temporary password from your welcome email"
                : "Choose a strong password to secure your account"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <AnimatePresence mode="wait">
              {step === "credentials" && (
                <motion.div
                  key="credentials"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <div className="mb-6">
                    <Button
                      variant="outline"
                      className="w-full h-12"
                      onClick={() =>
                        loginWithOIDCProvider(SupportedAuthProvider.GOOGLE)
                      }
                    >
                      <GoogleIcon />
                      <span className="ml-3">Continue with Google</span>
                    </Button>
                    <p className="text-xs text-center text-muted-foreground mt-2">
                      Use the same email address from your welcome email
                    </p>
                  </div>

                  <div className="relative mb-6">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">
                        Or use temporary password
                      </span>
                    </div>
                  </div>

                  <form
                    onSubmit={handleCredentialsSubmit}
                    className="space-y-4"
                  >
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={handleChange}
                        className={errors.email ? "border-destructive" : ""}
                      />
                      {errors.email && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="tempPassword">Temporary Password</Label>
                      <div className="relative">
                        <Input
                          id="tempPassword"
                          type={showTempPassword ? "text" : "password"}
                          placeholder="From your welcome email"
                          value={formData.tempPassword}
                          onChange={handleChange}
                          className={
                            errors.tempPassword
                              ? "border-destructive pr-10"
                              : "pr-10"
                          }
                        />
                        <button
                          type="button"
                          onClick={() => setShowTempPassword(!showTempPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showTempPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {errors.tempPassword && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.tempPassword}
                        </p>
                      )}
                    </div>

                    <div className="pt-2">
                      <CloudflareTurnstile
                        onVerify={handleTurnstileVerify}
                        onError={handleTurnstileError}
                        onExpire={handleTurnstileExpire}
                        theme="dark"
                      />
                      {errors.turnstile && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.turnstile}
                        </p>
                      )}
                    </div>

                    {submitError && (
                      <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                        <p className="text-sm text-destructive">
                          {submitError}
                        </p>
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full h-12"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          Continue
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>
                </motion.div>
              )}

              {step === "new-password" && (
                <motion.div
                  key="new-password"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <form
                    onSubmit={handleNewPasswordSubmit}
                    className="space-y-4"
                  >
                    <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 mb-4">
                      <p className="text-sm text-primary">
                        <strong>Account verified!</strong> Now create a secure
                        password.
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="newPassword">New Password</Label>
                      <div className="relative">
                        <Input
                          id="newPassword"
                          type={showNewPassword ? "text" : "password"}
                          placeholder="Create a strong password"
                          value={formData.newPassword}
                          onChange={handleChange}
                          className={
                            errors.newPassword
                              ? "border-destructive pr-10"
                              : "pr-10"
                          }
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showNewPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {errors.newPassword && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.newPassword}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Minimum 8 characters with uppercase, lowercase, and
                        numbers
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="confirmPassword">Confirm Password</Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm your password"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          className={
                            errors.confirmPassword
                              ? "border-destructive pr-10"
                              : "pr-10"
                          }
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.confirmPassword}
                        </p>
                      )}
                    </div>

                    {submitError && (
                      <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                        <p className="text-sm text-destructive">
                          {submitError}
                        </p>
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full h-12"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Setting password...
                        </>
                      ) : (
                        <>
                          Activate Account
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full"
                      onClick={() => {
                        setStep("credentials");
                        setSession(null);
                        setSubmitError(null);
                      }}
                    >
                      Back
                    </Button>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>

            <p className="text-xs text-center text-muted-foreground pt-2">
              By continuing, you agree to our{" "}
              <Link to="/legal/terms" className="text-primary hover:underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                to="/legal/privacy"
                className="text-primary hover:underline"
              >
                Privacy Policy
              </Link>
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Already have an account?{" "}
          <Link to="/login" className="text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </motion.div>

      {mfaSetup.isActive && mfaSetup.secretCode && mfaSetup.email && (
        <MfaSetupDialog
          open={true}
          onOpenChange={() => {}}
          secretCode={mfaSetup.secretCode}
          email={mfaSetup.email}
          onVerify={handleMfaSetupVerify}
          onComplete={handleMfaSetupComplete}
        />
      )}
    </div>
  );
}
